// src/App.js
import React, { useState, useEffect, createContext, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Public/Navbar';
import Footer from './components/Public/Footer';
import LanguageModal from './components/Public/LanguageModal';
import Login from './components/Login';
import ClaimDetailPage from './components/ClaimDetailPage';
import ClaimAgendaPage from './components/ClaimAgendaPage';
import Dashboard from './components/Dashboard/Dashboard';
import translations from './i18n/translations';
import './App.css';

// La web pública se descarga solo al entrar a "/": el panel no la arrastra.
// (El panel y la ficha comparten hojas de estilo; separarlos cambiaba el orden
// del CSS, por eso siguen en el paquete principal.)
const PublicWebsite = lazy(() => import('./components/Public/PublicWebsite'));

// Language Context
export const LanguageContext = createContext();
export const ThemeContext = createContext();

// Main App Component
const AppContent = () => {
  const [language, setLanguage] = useState('en');
  // El tema se lee al crear el estado (no en un efecto): así la primera
  // pintura, la pantalla de carga y la carga directa de una ficha ya usan el
  // tema elegido en vez de arrancar en oscuro y corregirse después.
  const [theme, setTheme] = useState(() => {
    try { const saved = localStorage.getItem('theme'); return saved === 'light' || saved === 'dark' ? saved : 'dark'; } catch { return 'dark'; }
  });
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, isAdjuster, loading } = useAuth();
  const location = useLocation();
  // Panel único por rol (2026-09-21): admin y ajustador caen los dos en
  // /dashboard (Dashboard.jsx decide qué mostrar por isAdmin), así que el
  // navbar/footer público se oculta ahí también, no solo en /claim-status
  // (que sigue existiendo para el detalle de reclamo, y como alias que
  // redirige a /dashboard) y en /schedule ("Mi agenda", antes
  // /claim-status/agenda — ver rutas abajo).
  const isClaimStatusPortal = location.pathname.startsWith('/claim-status')
    || location.pathname.startsWith('/dashboard')
    || location.pathname.startsWith('/schedule');

  useEffect(() => {
    try { localStorage.setItem('theme', theme); } catch { /* sin storage */ }
    document.body.className = `${theme}-mode`;
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const selectLanguage = (lang) => {
    setLanguage(lang);
    setShowLanguageModal(false);
    localStorage.setItem('languageSelected', 'true');
    localStorage.setItem('userLanguage', lang);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('userLanguage');
    if (savedLanguage && (savedLanguage === 'es' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
  }, []);

  const t = translations[language];

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <LanguageContext.Provider value={{ language, setLanguage }}>
        <Toaster
          position="top-right"
          containerStyle={{ zIndex: 10000 }}
          toastOptions={{
            duration: 4000,
            style: theme === 'light'
              ? {
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: '#1a1a2e',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  fontFamily: "'Rubik', sans-serif",
                  fontWeight: 500,
                  padding: '0.85rem 1rem'
                }
              : {
                  background: 'rgba(20, 20, 26, 0.95)',
                  color: '#ffffff',
                  border: '1px solid rgba(0, 128, 255, 0.25)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  fontFamily: "'Rubik', sans-serif",
                  fontWeight: 500,
                  padding: '0.85rem 1rem'
                },
            success: { iconTheme: { primary: '#0066ff', secondary: '#ffffff' } },
            error: { iconTheme: { primary: '#dc3545', secondary: '#ffffff' } }
          }}
        />
        {!isClaimStatusPortal && <LanguageModal isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} onSelectLanguage={selectLanguage} />}

        <div className="app">
          {!isClaimStatusPortal && <Navbar
            t={t}
            language={language}
            setLanguage={setLanguage}
            theme={theme}
            toggleTheme={toggleTheme}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            scrollToSection={scrollToSection}
            isAdjuster={isAdjuster}
          />}

          <Suspense fallback={<div className="loading-screen" role="status"><div className="spinner"></div></div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Panel único por rol (2026-09-21): un solo /dashboard para
                admin y ajustador — Dashboard.jsx decide qué mostrar según
                isAdmin, ya no hay un componente/URL separado por rol. */}
            <Route path="/dashboard" element={
              !isAuthenticated ? <Navigate to="/login" /> : <Dashboard />
            } />
            {/* Alias de compatibilidad: cualquier link/bookmark viejo a
                /claim-status (la URL que antes usaba el ajustador) cae al
                panel único. /claim-status/claim/:id NO se toca — sigue
                siendo su propia ruta, generada dinámicamente dentro de la
                app (ver ClaimsDirectory.jsx). */}
            <Route path="/claim-status" element={<Navigate to="/dashboard" />} />
            {/* "Mi agenda" — antes /claim-status/agenda, acortada a /schedule
                2026-09-21 (pedido explícito). Alias viejo redirige acá por
                si quedó algo bookmarkeado. */}
            <Route path="/schedule" element={<ClaimAgendaPage />} />
            <Route path="/claim-status/agenda" element={<Navigate to="/schedule" />} />
            <Route path="/claim-status/claim/:claimId" element={<ClaimDetailPage />} />
            <Route path="/" element={<PublicWebsite t={t} language={language} scrollToSection={scrollToSection} />} />
          </Routes>
          </Suspense>

          {!isClaimStatusPortal && <Footer t={t} language={language} scrollToSection={scrollToSection} />}
        </div>
      </LanguageContext.Provider>
    </ThemeContext.Provider>
  );
};

// App principal con AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
